import { useState, useCallback } from "react";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import { Label, LabelType } from "../../../api/label";
import useCollectionLabelsQuery from "../hooks/useCollectionLabelsQuery";
import useCollectionUpdateLabelMutation from "../hooks/useCollectionUpdateLabelMutation";
import useCollectionDeleteLabelMutation from "../hooks/useCollectionDeleteLabelMutation";
import useCreateCollectionLabelMutation from "../hooks/useCreateCollectionLabelMutation";
import useCreateCollectionLabelsBulkMutation from "../hooks/useCreateCollectionLabelsBulkMutation";
import CollectionLabelModal from "./CollectionLabelModal";
import CreateCollectionLabelModal from "./CreateCollectionLabelModal";
import { debounce } from "lodash";
import DSBox from "../../system-design/DSBox";
import DSTextField from "../../system-design/DSTextField";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";

function CollectionLabels() {
  const [textSearch, setTextSearch] = useState<string>("");
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sortState, setSortState] = useState<Record<string, 1 | -1>>({});

  const handleFreeSearch = useCallback(
    debounce((s) => setTextSearch(s), 500),
    [],
  );

  const {
    data: labels,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCollectionLabelsQuery(textSearch, 20, sortState);

  const updateMutation = useCollectionUpdateLabelMutation();
  const deleteMutation = useCollectionDeleteLabelMutation();
  const createMutation = useCreateCollectionLabelMutation();
  const createBulkMutation = useCreateCollectionLabelsBulkMutation();

  const labelTableColumns: Column<Label>[] = [
    { label: "Tag Text", field: "text", sortable: true },
    { label: "Type", field: "type", sortable: true },
    { label: "Created At", field: "createdAt", sortable: true },
    { label: "Updated At", field: "updatedAt", sortable: true },
  ];

  const rowBuilder = (label: Label, columns: Column<Label>[]) => {
    return (
      <>
        {columns.map((col) => {
          if (col.field === "text") {
            return (
              <DSTableCell key={col.field as string}>
                <strong>{label.text}</strong>
              </DSTableCell>
            );
          }
          if (col.field === "type") {
            return (
              <DSTableCell key={col.field as string}>
                <DSChip
                  label={label.type}
                  color={
                    label.type === LabelType.COLLECTION ? "primary" : "info"
                  }
                  size="small"
                />
              </DSTableCell>
            );
          }
          if (col.field === "createdAt") {
            return (
              <DSTableCell key={col.field as string}>
                {new Date(label.createdAt).toLocaleDateString()}
              </DSTableCell>
            );
          }
          if (col.field === "updatedAt") {
            return (
              <DSTableCell key={col.field as string}>
                {new Date(label.updatedAt).toLocaleDateString()}
              </DSTableCell>
            );
          }
          return <DSTableCell key={col.field as string}>-</DSTableCell>;
        })}
      </>
    );
  };

  const handleRowClick = (label: Label) => {
    setSelectedLabel(label);
  };

  const handleSort = (field: keyof Label) => {
    setSortState((prevState) => {
      const currentSort = prevState[field as string];
      if (!currentSort) {
        return { [field]: 1 };
      } else if (currentSort === 1) {
        return { [field]: -1 };
      } else {
        const newState = { ...prevState };
        delete newState[field as string];
        return newState;
      }
    });
  };

  const isInitialLoad = isLoading && !labels?.length && !textSearch;

  return (
    <>
      <DSBox mb={3} sx={{ marginRight: "20px" }}>
        <DSTextField
          key="search-input"
          fullWidth
          variant="outlined"
          placeholder="Search tag by name..."
          onChange={(e) => handleFreeSearch(e.target.value)}
        />
      </DSBox>

      <DSPrimaryButton
        onClick={() => setShowCreateModal(true)}
        sx={{
          width: "150px",
          marginBottom: "20px",
        }}
      >
        Add New Tag
      </DSPrimaryButton>

      {isInitialLoad ? (
        <DSBox sx={{ textAlign: "center", padding: "50px" }}>
          <DSCircularProgress />
        </DSBox>
      ) : (
        <>
          {isLoading && (
            <DSBox sx={{ textAlign: "center", padding: "10px" }}>
              <DSCircularProgress size={20} />{" "}
              <DSTypography variant="caption">Searching...</DSTypography>
            </DSBox>
          )}

          {labels && labels.length > 0 && (
            <DSBaseTable
              data={labels}
              columns={labelTableColumns}
              rowBuilder={rowBuilder}
              onPressRow={handleRowClick}
              onSort={handleSort}
              sortState={sortState}
              fetchMore={
                hasNextPage && !isFetchingNextPage ? fetchNextPage : undefined
              }
            />
          )}

          {labels && labels.length === 0 && !isLoading && (
            <DSBox sx={{ textAlign: "center", padding: "50px" }}>
              <DSTypography>No tags found</DSTypography>
            </DSBox>
          )}
        </>
      )}

      <CollectionLabelModal
        show={selectedLabel !== null}
        label={selectedLabel}
        onClose={() => setSelectedLabel(null)}
        updateMutation={updateMutation}
        deleteMutation={deleteMutation}
      />

      <CreateCollectionLabelModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        createMutation={createMutation}
        createBulkMutation={createBulkMutation}
      />
    </>
  );
}

export default CollectionLabels;
